"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, TrendingUp, TrendingDown, Minus } from "lucide-react";

interface SpeedKPIData {
  averageHours: number;
  previousAverageHours: number;
  percentChange: number;
  byMethod: {
    HEAT: number;
    ENG: number;
  };
}

interface ProductionSpeedKPIProps {
  accountId?: string | null;
}

export function ProductionSpeedKPI({ accountId }: ProductionSpeedKPIProps) {
  const [data, setData] = useState<SpeedKPIData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSpeedKPI();
  }, [accountId]);

  const fetchSpeedKPI = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (accountId) {
        params.append("accountId", accountId);
      }

      const response = await fetch(`/api/production-speed?${params}`);
      const result = await response.json();

      if (result.success) {
        setData(result.data);
        // Log debug info to browser console
        if (result.debug) {
          console.log("Production Speed Debug Info:", result.debug);
        }
      } else {
        console.error("Failed to fetch speed KPI:", result.error);
        setData(null);
      }
    } catch (error) {
      console.error("Error fetching speed KPI:", error);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (hours: number) => {
    if (hours < 24) {
      return `${hours.toFixed(1)} hrs`;
    } else {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      if (remainingHours < 1) {
        return `${days}d`;
      }
      return `${days}d ${remainingHours.toFixed(0)}h`;
    }
  };

  const getTrendIcon = () => {
    if (!data) return null;
    
    if (data.percentChange > 0) {
      // Positive change means slower (worse)
      return <TrendingUp className="h-5 w-5 text-red-500" />;
    } else if (data.percentChange < 0) {
      // Negative change means faster (better)
      return <TrendingDown className="h-5 w-5 text-green-500" />;
    } else {
      return <Minus className="h-5 w-5 text-gray-400" />;
    }
  };

  const getTrendColor = () => {
    if (!data) return "text-gray-500";
    if (data.percentChange > 0) return "text-red-500";
    if (data.percentChange < 0) return "text-green-500";
    return "text-gray-500";
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="text-base sm:text-lg">Production Speed</CardTitle>
            <CardDescription className="text-xs sm:text-sm">Average time from import to binned</CardDescription>
          </div>
          <div className="px-3 py-1 bg-slate-100 rounded-md self-start sm:self-auto">
            <span className="text-xs sm:text-sm font-medium text-slate-700">7 days</span>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex flex-col items-center justify-center p-4 sm:p-8">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mb-2" />
            <span className="text-xs sm:text-sm text-slate-500">Loading speed metrics...</span>
          </div>
        ) : data ? (
          <div className="space-y-4 sm:space-y-6">
            {/* Main Metric */}
            <div className="flex items-center justify-center gap-4">
              <div className="text-center">
                <div className="text-3xl sm:text-5xl font-bold text-gray-900">{formatDuration(data.averageHours)}</div>
                <div className={`flex items-center justify-center gap-2 mt-2 ${getTrendColor()}`}>
                  {getTrendIcon()}
                  <span className="text-xs sm:text-sm font-medium">
                    {Math.abs(data.percentChange).toFixed(1)}% vs previous week
                  </span>
                </div>
              </div>
            </div>

            {/* Breakdown by Decoration Method */}
            <div className="border-t pt-3 sm:pt-4">
              <h4 className="text-xs sm:text-sm font-medium text-gray-700 mb-2 sm:mb-3">By Decoration Method</h4>
              <div className="grid grid-cols-2 gap-2 sm:gap-3">
                {/* HEAT */}
                <div className="flex flex-col items-center p-2 sm:p-3 bg-orange-50 rounded-md">
                  <span className="text-xs text-orange-600 font-medium mb-1">HEAT</span>
                  <span className="text-sm sm:text-lg font-bold text-orange-700">
                    {data.byMethod.HEAT > 0 ? formatDuration(data.byMethod.HEAT) : "N/A"}
                  </span>
                </div>
                {/* ENG */}
                <div className="flex flex-col items-center p-2 sm:p-3 bg-blue-50 rounded-md">
                  <span className="text-xs text-blue-600 font-medium mb-1">ENG</span>
                  <span className="text-sm sm:text-lg font-bold text-blue-700">
                    {data.byMethod.ENG > 0 ? formatDuration(data.byMethod.ENG) : "N/A"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center p-4 sm:p-8">
            <p className="text-xs sm:text-sm text-slate-500">No speed data available</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

